import {
    AnalyticsCartTicket, CartTicket,
    HoldState,
    PriceLevelState,
    PromotionHoldState,
    PromotionHoldStateProvider,
    SelectableInfo,
    StructuredPricelevel,
    TicketTypeState,
    TicketTypeStateProvider
} from "@/domain/ticket-selection";
import { HoldStateProvider } from "@/domain/ticket-selection/data/providers/HoldState.provider";

/**
 * PriceLevelStateProvider
 * data provider class for PriceLevel price list level
 */
export class PriceLevelStateProvider {

    public static getActivePromotionHoldFor(priceLevel: PriceLevelState): PromotionHoldState | undefined {
        return priceLevel.promotionHolds.find((promotionHold: PromotionHoldState) => promotionHold.active);
    }

    public static getActiveHoldFor(priceLevel: PriceLevelState): HoldState | undefined {
        const promotionHold = this.getActivePromotionHoldFor(priceLevel);
        if (!promotionHold) {
            return undefined;
        }

        return promotionHold.holds.find((hold: HoldState) => hold.active);
    }

    /**
     * whatItDoes
     * sets PriceLevelState to active and returns updated state
     */
    public static activate(priceLevel: PriceLevelState): PriceLevelState {
        priceLevel.active = true;

        return priceLevel;
    }

    /**
     * whatItDoes
     * sets PriceLevelState to inactive, closed and returns updated state
     */
    public static deactivate(priceLevel: PriceLevelState): PriceLevelState {
        priceLevel.active = false;
        priceLevel.open = false;

        return priceLevel;
    }

    /**
     * whatItDoes
     * toggles PriceLevelState open property and returns updated state
     */
    public static toggle(priceLevel: PriceLevelState, shouldOpen?: boolean): PriceLevelState {
        if (shouldOpen !== undefined) {
            priceLevel.open = shouldOpen;
        } else {
            priceLevel.open = !priceLevel.open;
        }

        return priceLevel;
    }

    /**
     * whatItDoes
     * updates active PriceLevelState PromotionHolds with TicketType state update
     */
    public static updatedPriceLevelForActiveTicketTypes(
        updatedState: TicketTypeState[],
        priceLevel: PriceLevelState
    ): PriceLevelState {
        if (priceLevel.active && priceLevel.promotionHolds) {
            priceLevel.promotionHolds = priceLevel.promotionHolds.map(promotionHold =>
                PromotionHoldStateProvider.updatedPromotionHoldForActiveTicketTypes(updatedState, promotionHold)
            )
        }

        return priceLevel;
    }

    /**
     * whatItDoes
     * updates priceLevel submit state based on hold and ticket type restriction validity checks
     * @param priceLevel
     */
    public static updatedSubmitStateFor(priceLevel: PriceLevelState): PriceLevelState {
        const activeHold = this.getActiveHoldFor(priceLevel);

        if (!activeHold) {
            return priceLevel;
        }

        priceLevel.canSubmit = HoldStateProvider.isAmountValidFor(activeHold)
            && HoldStateProvider.areTicketTypesValidIn(activeHold);

        return priceLevel;
    }

    /**
     * whatItDoes
     * provides add to cart url query for added TicketTypes
     */
    public static getAddToCartTicketTypesUrlFragmentFor(priceLevel: PriceLevelState): string {
        const activeHold = this.getActiveHoldFor(priceLevel);

        return !activeHold ? '' : activeHold.ticketTypes
            .filter(ticketType => ticketType.amount > 0)
            .map(ticketType => TicketTypeStateProvider.provideEncodedUriFragmentFor(ticketType, priceLevel.priceLevelId))
            .reduce((pre, cur) => !pre ? cur : `${pre}&${cur}`, '');
    }

    /**
     * whatItDoes
     * provides array of selected tickets
     */
    public static getSelectedTickets(priceLevel: PriceLevelState): TicketTypeState[] | undefined {
        const activeHold = this.getActiveHoldFor(priceLevel);

        return !activeHold ? undefined : activeHold.ticketTypes
            .filter(ticketType => ticketType.amount > 0);
    }

    public static getAnalyticsCartTicketsFromBestSeat(priceLevel: PriceLevelState): AnalyticsCartTicket {
        const activeHold = this.getActiveHoldFor(priceLevel);

        if (!activeHold) {
            return {ticketTypes: []};
        }

        return {
            ticketTypes: this.getSelectedTickets(priceLevel) ?
                this.getSelectedTickets(priceLevel)!.map(ticket => ({
                        price: ticket.price,
                        name: ticket.text,
                        amount: ticket.amount,
                        priceCategoryId: priceLevel.priceLevelId,
                        priceCategoryName: priceLevel.text,
                        text: ticket.text,
                    }),
                )
                : [],
        }
    }

    public static getAnalyticsCartTicketsFromSeatmap(data: StructuredPricelevel[]): AnalyticsCartTicket {
        const groupedByPriceId = {};

        data.forEach(item => {
            item.selectionInfo.ticketTypes.filter(type => type.active).forEach(type => {
                const {priceId, price, name} = type;
                const key = priceId;

                if (!groupedByPriceId[key]) {
                    groupedByPriceId[key] = {
                        amount: 0,
                        priceCategoryId: item.selectionInfo.priceCategoryId,
                        priceCategoryName: item.selectionInfo.priceCategoryName,
                        price,
                        name
                    };
                }

                groupedByPriceId[key].amount += 1;
            });
        });

        return {
            ticketTypes: Object.values(groupedByPriceId),
        };
    }

    public static getSelectedTicketsValue(priceLevel: PriceLevelState): number {
        const tickets = this.getSelectedTickets(priceLevel);
        if (!tickets) {
            return 0;
        }

        return tickets.reduce((acc, curr) => acc + (curr.price * curr.amount), 0);
    }

}
